import { CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { PlanTypes, PlanTypesDisplay } from 'mbs-ui-shared@models/PlanTypes.enum';
import { ConsistencyCheckModelForStepsHelper } from 'wizards/helpers/consistency-check-helpers/consistency-check-model-for-steps-helper';
import { ConsistencyCheckPlanFromStepsHelper } from 'wizards/helpers/consistency-check-helpers/consistency-check-plan-from-steps-helper';
import { ConsistencyCheckModel } from 'wizards/models/consistency-check/consistency-check-model';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardBaseClass } from 'wizards/remote-management-wizards/wizard-base.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { CompressionEncryptionStepComponent } from 'wizards/steps/compression-encryption-step/compression-encryption-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

type ConsistencyCheckWizardHelpDocumentationLinks = 'mainLink';

@UntilDestroy()
@Component({
  selector: 'mbs-consistency-check-wizard',
  templateUrl: './consistency-check-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => ConsistencyCheckWizardComponent)
    }
  ],
  imports: [
    CommonModule,
    CompressionEncryptionStepComponent,
    FormsModule,
    NBFScheduleStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe
  ]
})
export class ConsistencyCheckWizardComponent extends WizardBaseClass {
  private modalService = inject(ModalService);
  i18nPipe: I18NextPipe;

  public planName = PlanTypesDisplay[PlanTypes.ConsistencyCheck];
  public helpLink: WizardHelpLink<ConsistencyCheckWizardHelpDocumentationLinks>;

  constructor() {
    const services = inject(WizardsServicesHubService);
    const i18nPipe = inject(I18NextPipe);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new ConsistencyCheckModel();

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) return this.setDefaultValue();

    this.FormModel = ConsistencyCheckModelForStepsHelper.consistencyCheckModelFromPlan(plan, this.defaultSettings());

    if (this.isEdit) this.planName = plan?.plan?.Name || PlanTypesDisplay[PlanTypes.ConsistencyCheck];
  }

  protected getPlanForSaving(): any {
    return ConsistencyCheckPlanFromStepsHelper.getUpdatedPlan(this.loadedPlan.plan, this.FormModel, this.defaultSettings());
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected loadInfoHandlerFinally() {}

  getRAWHelpLinks(): Record<ConsistencyCheckWizardHelpDocumentationLinks, string> {
    return { mainLink: `/backup/about/mandatory-full-consistency-checks` };
  }
}
